import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  SetStateAction
} from "react";
import { Store } from "redux";
import { Icon } from "@tvg/design-system";
import buildColor from "@tvg/atomic-ui/_static/ColorPalette";
import { fontNormal } from "@tvg/atomic-ui/_static/Typography";
import { useBraintreeHostedFields } from "@tvg/sh-lib-paws/hooks/useBraintreeHostedFields";
import { walletFormMessages } from "@tvg/sh-lib-paws/redux/defaultValues";
import {
  HostedFieldState,
  HostedFieldsTokenizePayload
} from "@tvg/sh-lib-paws/types/braintree";
import {
  BRAINTREE_DISCLAIMER_FALLBACK,
  CARDHOLDER_NAME_MAX_LENGTH
} from "@tvg/sh-lib-paws/constants";
import { useSelector } from "react-redux";
import { get } from "lodash";
import { renderError } from "../FundsModal/common/InputField/methods";
import { CreditDebitCardCreation, ErrorMessage } from "../../types";
import SpinningWheel from "../SpinningWheel";
import {
  Container,
  FieldGroup,
  Label,
  FieldContainer,
  CvvIconContainer,
  Row,
  Col,
  LoadingContainer,
  Disclaimer,
  Hint
} from "./styled-components";

export const CARDHOLDER_NAME_HINT = `Maximum ${CARDHOLDER_NAME_MAX_LENGTH} characters`;
export const CARDHOLDER_NAME_TOO_LONG_ERROR = `Name on card must be ${CARDHOLDER_NAME_MAX_LENGTH} characters or fewer`;

export interface BraintreeHostedFieldsRef {
  tokenize: () => Promise<HostedFieldsTokenizePayload>;
  isReady: boolean;
  deviceData: string;
  allFieldsValid: boolean;
  /** Flags the cardholder name as too long after the gateway rejects it */
  showCardholderNameTooLong: () => void;
}

interface BraintreeHostedFieldsProps {
  isModalOpen: boolean;
  setErrorMessage: React.Dispatch<SetStateAction<ErrorMessage>>;
  content?: CreditDebitCardCreation;
  onFieldFocus?: (fieldName: string) => void;
  onFieldBlur?: (fieldName: string) => void;
  onFieldsValidityChange?: (allValid: boolean) => void;
  onCvvIconClick?: () => void;
}

const BraintreeHostedFields = forwardRef<
  BraintreeHostedFieldsRef,
  BraintreeHostedFieldsProps
>(
  (
    {
      isModalOpen,
      setErrorMessage,
      content = walletFormMessages.creditDebitCardCreation,
      onFieldFocus,
      onFieldBlur,
      onFieldsValidityChange,
      onCvvIconClick
    },
    ref
  ) => {
    const disclaimerMsg = useSelector((store: Store) =>
      get(
        store,
        "capi.messages.braintreeDisclaimer",
        BRAINTREE_DISCLAIMER_FALLBACK
      )
    );

    const hostedFieldStyles = useMemo(
      () => ({
        input: {
          "font-size": "14px",
          "font-family": fontNormal,
          "font-weight": "400",
          color: buildColor("grey", "900"),
          "line-height": "22px"
        },
        "input.invalid": {
          color: buildColor("orange", "600")
        },
        "input.valid": {
          color: buildColor("grey", "900")
        },
        "input::placeholder": {
          color: buildColor("grey", "700")
        }
      }),
      []
    );

    const [visitedFields, setVisitedFields] = useState<Set<string>>(new Set());
    // Set from a gateway rejection; the iframe can't report the value length,
    // so this clears as soon as the user edits the field again.
    const [isCardholderNameTooLong, setIsCardholderNameTooLong] =
      useState(false);

    const handleFieldFocus = useCallback(
      (fieldName: string) => {
        if (fieldName === "cardholderName") {
          setIsCardholderNameTooLong(false);
        }
        onFieldFocus?.(fieldName);
      },
      [onFieldFocus]
    );

    const handleFieldBlur = useCallback(
      (fieldName: string) => {
        setVisitedFields((prev) => {
          if (prev.has(fieldName)) return prev;
          const next = new Set(prev);
          next.add(fieldName);
          return next;
        });
        onFieldBlur?.(fieldName);
      },
      [onFieldBlur]
    );

    const {
      isReady,
      isLoading,
      fieldStates,
      deviceData,
      tokenize,
      containerRef
    } = useBraintreeHostedFields({
      isModalOpen,
      styles: hostedFieldStyles,
      setErrorMessage,
      onFieldFocus: handleFieldFocus,
      onFieldBlur: handleFieldBlur
    });

    const allFieldsValid =
      fieldStates.cardholderName.isValid &&
      fieldStates.number.isValid &&
      fieldStates.expirationDate.isValid &&
      fieldStates.cvv.isValid;

    useEffect(() => {
      onFieldsValidityChange?.(allFieldsValid);
    }, [allFieldsValid, onFieldsValidityChange]);

    const showCardholderNameTooLong = useCallback(
      () => setIsCardholderNameTooLong(true),
      []
    );

    useImperativeHandle(
      ref,
      () => ({
        tokenize,
        isReady,
        deviceData,
        allFieldsValid,
        showCardholderNameTooLong
      }),
      [tokenize, isReady, deviceData, allFieldsValid, showCardholderNameTooLong]
    );

    const defaults = walletFormMessages.creditDebitCardCreation;
    const cardNumber = content?.cardNumber ?? defaults.cardNumber;
    const expirationDateCard =
      content?.expirationDateCard ?? defaults.expirationDateCard;
    const cvv = content?.cvv ?? defaults.cvv;

    const getFieldError = (
      field: HostedFieldState,
      fieldName: string,
      requiredMsg: string,
      invalidMsg: string
    ): string | undefined => {
      if (!visitedFields.has(fieldName) || field.isFocused) return undefined;
      if (field.isEmpty) return requiredMsg;
      if (!field.isValid) return invalidMsg;
      return undefined;
    };

    const cardholderNameError = isCardholderNameTooLong
      ? CARDHOLDER_NAME_TOO_LONG_ERROR
      : getFieldError(
          fieldStates.cardholderName,
          "cardholderName",
          "Name on card is required",
          "Enter the name as it appears on your card"
        );
    const numberError = getFieldError(
      fieldStates.number,
      "number",
      cardNumber.validations.required,
      cardNumber.validations.invalid
    );
    const expirationError = getFieldError(
      fieldStates.expirationDate,
      "expirationDate",
      expirationDateCard.validations.required,
      expirationDateCard.validations.invalidDate
    );
    const cvvError = getFieldError(
      fieldStates.cvv,
      "cvv",
      cvv.validations.required,
      cvv.validations.invalid
    );

    return (
      <Container data-qa-label="braintree-hosted-fields">
        {(isLoading || !isReady) && (
          <LoadingContainer data-qa-label="braintree-hosted-fields-loading">
            <SpinningWheel />
          </LoadingContainer>
        )}

        <FieldGroup isHidden={!isReady}>
          <Label
            id="label-braintree-cardholder-name"
            data-qa-label="label-braintree-cardholder-name"
          >
            Name on Card
          </Label>
          <FieldContainer
            id="braintree-cardholder-name"
            role="group"
            aria-labelledby="label-braintree-cardholder-name"
            data-qa-label="braintree-cardholder-name"
            isFocused={fieldStates.cardholderName.isFocused}
            hasError={!!cardholderNameError}
          />
          {cardholderNameError ? (
            renderError(cardholderNameError, "braintree-cardholder-name")
          ) : (
            <Hint data-qa-label="braintree-cardholder-name-hint">
              {CARDHOLDER_NAME_HINT}
            </Hint>
          )}
        </FieldGroup>

        <FieldGroup isHidden={!isReady}>
          {disclaimerMsg && <Disclaimer>{disclaimerMsg}</Disclaimer>}

          <Label
            id="label-braintree-card-number"
            data-qa-label="label-braintree-card-number"
          >
            Card Number
          </Label>
          <FieldContainer
            ref={containerRef}
            id="braintree-card-number"
            role="group"
            aria-labelledby="label-braintree-card-number"
            data-qa-label="braintree-card-number"
            isFocused={fieldStates.number.isFocused}
            hasError={!!numberError}
          />
          {numberError && renderError(numberError, "braintree-card-number")}
        </FieldGroup>

        <Row isHidden={!isReady}>
          <Col>
            <FieldGroup isHidden={!isReady}>
              <Label
                id="label-braintree-expiration-date"
                data-qa-label="label-braintree-expiration-date"
              >
                Expiration Date
              </Label>
              <FieldContainer
                id="braintree-expiration-date"
                role="group"
                aria-labelledby="label-braintree-expiration-date"
                data-qa-label="braintree-expiration-date"
                isFocused={fieldStates.expirationDate.isFocused}
                hasError={!!expirationError}
              />
              {expirationError &&
                renderError(expirationError, "braintree-expiration-date")}
            </FieldGroup>
          </Col>
          <Col>
            <FieldGroup isHidden={!isReady}>
              <Label
                id="label-braintree-cvv"
                data-qa-label="label-braintree-cvv"
              >
                CVV
              </Label>
              <FieldContainer
                id="braintree-cvv"
                role="group"
                aria-labelledby="label-braintree-cvv"
                data-qa-label="braintree-cvv"
                isFocused={fieldStates.cvv.isFocused}
                hasError={!!cvvError}
              >
                {isReady && onCvvIconClick && (
                  <CvvIconContainer
                    data-qa-label="braintree-cvv-icon"
                    role="button"
                    tabIndex={0}
                    aria-label="CVV information"
                    onMouseDown={(event) => event.preventDefault()}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onCvvIconClick();
                      }
                    }}
                    onClick={onCvvIconClick}
                  >
                    <Icon
                      name="cvv"
                      size="xl"
                      lineColor={buildColor("blue_accent", "500")}
                    />
                  </CvvIconContainer>
                )}
              </FieldContainer>
              {cvvError && renderError(cvvError, "braintree-cvv")}
            </FieldGroup>
          </Col>
        </Row>
      </Container>
    );
  }
);

BraintreeHostedFields.displayName = "BraintreeHostedFields";

export default BraintreeHostedFields;
